import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  Building2,
  User,
  Bell,
  Database,
  Download,
  Upload,
  Save,
  Settings as SettingsIcon,
  Lock,
  Search,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import CompanySettingsTab from "@/components/admin/settings/CompanySettingsTab";
import PayrollSettingsTab from "@/components/admin/settings/PayrollSettingsTab";
import NotificationSettingsTab from "@/components/admin/settings/NotificationSettingsTab";
import SecuritySettingsTab from "@/components/admin/settings/SecuritySettingsTab";
import {
  changePasswordService,
  createSettings,
  getSettings,
  fetchCompanyRatingConfiguration,
  uploadCompanyLogo,
} from "@/services/adminService";
import { invalidateRatingConfig } from "@/hooks/useRatingConfig";
import BankAccountsTab from "@/components/admin/settings/BankAccountsTab";
import WorkLocationDeptDesignationTab from "@/components/admin/settings/WorkLocationDeptDesignation";
import ManagerDutiesCard from "@/components/admin/settings/ManagerDutiesCard";
import MailSettingsTab from "@/components/admin/settings/MailSettingsTab";
import {
  validatePAN,
  validateTAN,
  validateGSTIN,
  validateCIN,
  validatePFRegistration,
  validateESIRegistration,
} from "@/lib/validations"; // adjust path as needed
import { useLocation } from "react-router-dom";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { usePermission } from "@/contexts/RoleAccessContext";
import { Input } from "@/components/ui/input";

// --- SETTINGS SEARCH MAP (Moved before use for ReferenceError fix) ---
const SETTINGS_MAP = [
  /* ===================== COMPANY TAB ===================== */
  {
    tab: "company",
    name: "Company Name",
    elementId: "setting-company-name",
  },
  {
    tab: "company",
    name: "Company Logo",
    elementId: "setting-company-logo",
  },
  {
    tab: "company",
    name: "Registered Address",
    elementId: "setting-company-address",
  },
  {
    tab: "company",
    name: "PAN Number",
    elementId: "setting-company-pan",
  },
  {
    tab: "company",
    name: "GSTIN",
    elementId: "setting-company-gstin",
  },
  {
    tab: "company",
    name: "CIN Number",
    elementId: "setting-company-cin",
  },

  /* ===================== Rating and overtime ===================== */
  {
    tab: "configurations",
    name: "Employee Portal",
    elementId: "setting-employee-portal",
  },
  {
    tab: "configurations",
    name: "Daily Punch In/Out",
    elementId: "setting-rating/punch",
  },
  {
    tab: "configurations",
    name: "Overtime Configuration",
    elementId: "setting-overtime",
  },
  {
    tab: "configurations",
    name: "Rating Configuration",
    elementId: "setting-rating",
  },

  /* ===================== BANK & LOANS TAB ===================== */
  {
    tab: "bank",
    name: "Bank Accounts",
    elementId: "setting-bank-accounts",
  },
  {
    tab: "bank",
    name: "Loan Types",
    elementId: "setting-loan-types",
  },

  /* ===================== ORG STRUCTURE TAB ===================== */
  {
    tab: "org-structure",
    name: "Work Locations",
    elementId: "setting-org-locations",
  },
  {
    tab: "org-structure",
    name: "Departments",
    elementId: "setting-org-departments",
  },
  {
    tab: "org-structure",
    name: "Designations",
    elementId: "setting-org-designations",
  },
];

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const fileInputRef = React.useRef(null);
  const [companySettings, setCompanySettings] = useState();
  const [payrollSettings, setPayrollSettings] = useState();
  const [holidays, setHolidays] = useState([]);
  const [ratingConfig, setRatingConfig] = useState();
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    payroll_reminders: true,
    compliance_alerts: true,
    birthday_reminders: true,
    leave_notifications: true,
    loan_reminders: true,
  });

  const [securitySettings, setSecuritySettings] = useState({
    two_factor_auth: false,
    password_policy: "medium",
    session_timeout: 30,
    audit_logging: true,
    data_encryption: true,
    backup_frequency: "daily",
  });

  const [uiSettings, setUiSettings] = useState({
    theme: "light",
    language: "english",
    date_format: "dd/mm/yyyy",
    currency_format: "inr",
    timezone: "ist",
  });

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [changingPassword, setChangingPassword] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [allLoanTypes, setAllLoanTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [companyCheckedInValue, setCompanyCheckedInValue] = useState(null);
  const [companyEmployeePortalEnabled, setCompanyEmployeePortalEnabled] = useState(null);

  const { reloadOnboardingStatus } = useOnboarding();
  const location = useLocation();
  // Set default tab from location state
  const defaultTab = location.state?.tab || "company";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { can } = usePermission();

  const [ratingConfigDraft, setRatingConfigDraft] = useState(null);

  useEffect(() => {
    return () => {
      if (companySettings?.logo_url?.startsWith("blob:")) {
        URL.revokeObjectURL(companySettings.logo_url);
      }
    };
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      return;
    }

    const results = SETTINGS_MAP.filter((s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 7);

    setSearchResults(results);
  }, [searchTerm]);

  const handleResultClick = (result) => {
    setSearchTerm("");
    setSearchResults([]);

    // Switch Tab
    setActiveTab(result.tab);

    // Wait for tab to render, then scroll + highlight
    setTimeout(() => {
      const targetElement = document.getElementById(result.elementId);

      if (!targetElement) {
        toast.warning(
          `Setting found, but could not scroll to "${result.name}". Add id="${result.elementId}" in that section.`
        );
        return;
      }

      const topPosition =
        targetElement.getBoundingClientRect().top + window.scrollY - 100;

      window.scrollTo({ top: topPosition, behavior: "smooth" });

      const originalStyle = targetElement.style.cssText;

      targetElement.style.transition = "all 0.3s ease-in-out";
      targetElement.style.border = "2px solid #F59E0B";
      targetElement.style.boxShadow = "0 0 10px rgba(245, 158, 11, 0.5)";
      targetElement.style.borderRadius = "0.5rem";
      targetElement.style.padding = "0.5rem";

      setTimeout(() => {
        targetElement.style.cssText = originalStyle;
      }, 3000);
    }, 150);
  };

  const tabPermissions = {
    company: "settings_company_configuration",
    configurations: "attendance_and_policy_configuration",
    notifications: "settings_notification_configuration",
    security: "settings_security_configuration",
    bank: "settings_bank_configuration",
    "org-structure": "work_locations_configuration",
    // Mail settings reuse the company-configuration permission (same admins).
    mail: "settings_company_configuration",
  };

  const canViewTab = (tabKey) => {
    const feature = tabPermissions[tabKey];
    if (!feature) return true; // fallback
    return can(feature, "view");
  };

  useEffect(() => {
    fetchSettings();
    fetchRatings();
  }, []);

  useEffect(() => {
    if (!canViewTab(activeTab)) {
      const firstAllowedTab = Object.keys(tabPermissions).find((tab) =>
        canViewTab(tab),
      );

      if (firstAllowedTab) {
        setActiveTab(firstAllowedTab);
      }
    }
  }, [activeTab]);

  const fetchRatings = async () => {
    try {
      const res = await fetchCompanyRatingConfiguration();
      if (!res) return;
      setRatingConfig(res);
      setRatingConfigDraft(res);
    } catch (error) {
      console.error("Error fetching rating config:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);

      const { data: settings } = await getSettings();

      // API returns top-level fields
      setCompanySettings(settings.company_settings || {});
      setPayrollSettings(settings.payroll_settings || {});
      setHolidays(settings.holidays || []);
      setNotificationSettings(settings.notification_settings || {});
      setSecuritySettings(settings.security_settings || {});
      setUiSettings(settings.ui_settings || {});
      setBankAccounts(settings.bank_accounts || []);
      setAllLoanTypes(settings.loan_types || []);
      setLocations(settings.work_locations || []);
      setDepartments(settings.departments || []);
      setDesignations(settings.designations || []);
      setCompanyCheckedInValue(settings.isCheckinOutEnabled || false);
      setCompanyEmployeePortalEnabled(settings.employee_portal_enabled || false);
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    // setDownloading(true);
    // try {
    //   const response = await axios.get(`${API}/backup/download`, {
    //     responseType: "blob",
    //   });
    //   // Create download link
    //   const blob = new Blob([response.data], { type: "application/json" });
    //   const url = window.URL.createObjectURL(blob);
    //   const link = document.createElement("a");
    //   link.href = url;
    //   link.download = `payroll_backup_${
    //     new Date().toISOString().split("T")[0]
    //   }.json`;
    //   document.body.appendChild(link);
    //   link.click();
    //   document.body.removeChild(link);
    //   window.URL.revokeObjectURL(url);
    //   toast.success("Backup downloaded successfully!");
    //   await fetchBackupInfo();
    // } catch (error) {
    //   console.error("Error downloading backup:", error);
    //   toast.error("Failed to download backup");
    // } finally {
    //   setDownloading(false);
    // }
  };

  const handleRestoreBackup = async (event) => {
    // const file = event.target.files[0];
    // if (!file) return;
    // if (!file.name.endsWith(".json")) {
    //   toast.error("Please select a valid JSON backup file");
    //   return;
    // }
    // if (
    //   !window.confirm(
    //     "⚠️ WARNING: This will restore all data from the backup file. Current data may be affected. Are you sure you want to continue?"
    //   )
    // ) {
    //   event.target.value = null;
    //   return;
    // }
    // setRestoring(true);
    // try {
    //   const formData = new FormData();
    //   formData.append("file", file);
    //   const response = await axios.post(`${API}/backup/restore`, formData, {
    //     headers: {
    //       "Content-Type": "multipart/form-data",
    //     },
    //   });
    //   toast.success(
    //     `Backup restored successfully! ${response.data.stats.total_documents} documents restored.`
    //   );
    //   await fetchBackupInfo();
    //   // Optionally reload the page to reflect changes
    //   setTimeout(() => {
    //     window.location.reload();
    //   }, 2000);
    // } catch (error) {
    //   console.error("Error restoring backup:", error);
    //   toast.error(error.response?.data?.detail || "Failed to restore backup");
    // } finally {
    //   setRestoring(false);
    //   event.target.value = null;
    // }
  };

  const handleCompanySettingChange = (field, value) => {
    setCompanySettings((prev) => ({ ...prev, [field]: value }));
  };

  const handlePayrollSettingChange = (field, value) => {
    setPayrollSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (field, value) => {
    setNotificationSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecurityChange = (field, value) => {
    setSecuritySettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleUiChange = (field, value) => {
    setUiSettings((prev) => ({ ...prev, [field]: value }));
  };

  const validateCompanyFields = () => {
    const errors = [];

    if (companySettings.pan && !validatePAN(companySettings.pan)) {
      errors.push("Invalid PAN number.");
    }

    if (companySettings.tan && !validateTAN(companySettings.tan)) {
      errors.push("Invalid TAN number.");
    }

    if (companySettings.gstin && !validateGSTIN(companySettings.gstin)) {
      errors.push("Invalid GSTIN.");
    }

    if (companySettings.cin && !validateCIN(companySettings.cin)) {
      errors.push("Invalid CIN.");
    }

    if (
      companySettings.pf_registration &&
      !validatePFRegistration(companySettings.pf_registration)
    ) {
      errors.push("Invalid PF Registration number.");
    }

    if (
      companySettings.esi_registration &&
      !validateESIRegistration(companySettings.esi_registration)
    ) {
      errors.push("Invalid ESI Registration number.");
    }

    return errors;
  };

  const validateRatingBeforeSaveAll = (ratingData) => {
    const requiredFields = [
      "base_rating",
      "late_arrival",
      "overtime",
      "punctuality_bonus",
      "max_rating",
    ];

    for (const field of requiredFields) {
      if (
        ratingData[field] === "" ||
        ratingData[field] === null ||
        ratingData[field] === undefined
      ) {
        toast.warning(`${field.replace("_", " ")} is required`);
        return false;
      }
    }

    const baseRating = Number(ratingData.base_rating);
    const lateArrival = Number(ratingData.late_arrival);
    const overtime = Number(ratingData.overtime);
    const punctualityBonus = Number(ratingData.punctuality_bonus);
    const maxRating = Number(ratingData.max_rating);

    if (
      [baseRating, lateArrival, overtime, punctualityBonus, maxRating].some(
        (v) => Number.isNaN(v)
      )
    ) {
      toast.error("Invalid numeric values detected");
      return false;
    }

    if (baseRating < 1 || baseRating > 10) {
      toast.warning("Base rating must be between 1 and 10");
      return false;
    }

    if (maxRating < 0 || maxRating > 10) {
      toast.warning("Max rating must be between 0 and 10");
      return false;
    }

    if (baseRating > maxRating) {
      toast.warning("Base rating cannot be greater than max rating");
      return false;
    }

    if (lateArrival < 0) {
      toast.warning("Late arrival penalty cannot be negative");
      return false;
    }

    if (overtime < 0) {
      toast.warning("Overtime reward cannot be negative");
      return false;
    }

    if (punctualityBonus < 0) {
      toast.warning("Punctuality bonus cannot be negative");
      return false;
    }

    // ✅ Values must not exceed max_rating
    const exceedMax = [
      { label: "Late arrival penalty", value: lateArrival },
      { label: "Overtime reward", value: overtime },
      { label: "Punctuality bonus", value: punctualityBonus },
    ].find((item) => item.value > maxRating);

    if (exceedMax) {
      toast.warning(
        `${exceedMax.label} cannot exceed max rating (${maxRating})`
      );
      return false;
    }

    return true; // ✅ VALID
  };

  const handleSaveSettings = async () => {
    const validationErrors = validateCompanyFields();

    if (validationErrors.length > 0) {
      toast.error(
        validationErrors.length === 1
          ? validationErrors[0]
          : "Please correct the highlighted fields before saving."
      );
      return;
    }

    if (!validateRatingBeforeSaveAll(ratingConfigDraft)) {
      return;
    }

    if (companyLogoFile && companyLogoFile.size > 5 * 1024 * 1024) {
      // 5MB limit
      toast.error("Logo file size must be less than 5MB");
      return;
    }

    setSaving(true);
    try {
      let logoUrl = null;
      if (companyLogoFile) {
        const logoFormData = new FormData();
        logoFormData.append("companyLogo", companyLogoFile);

        const logoResponse = await uploadCompanyLogo(logoFormData); // Separate endpoint
        logoUrl = logoResponse.data.logo_url;
      }

      const formData = new FormData();

      // Add file if selected
      if (companyLogoFile) {
        formData.append("companyLogo", companyLogoFile);
      }

    const settingsPayload = {
      company_settings: { ...companySettings, logo_url: logoUrl },
      payroll_settings: payrollSettings,
      notification_settings: notificationSettings,
      security_settings: securitySettings,
      ui_settings: uiSettings,
      holidays: holidays,
      bank_accounts: bankAccounts,
      locations: locations,
      departments: departments,
      designations: designations,
      rating_config: ratingConfigDraft,
    };

      await createSettings(settingsPayload);
      // rating_config is cached for the session, and changing base_rating or
      // max_rating moves every performance band — drop the cache so admin
      // screens pick the new scale up without a reload.
      invalidateRatingConfig();
      reloadOnboardingStatus();
      toast.success("All settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const exportSettings = () => {
    const allSettings = {
      company: companySettings,
      configurations: payrollSettings,
      notifications: notificationSettings,
      security: securitySettings,
      ui: uiSettings,
    };

    const dataStr = JSON.stringify(allSettings, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payroll-settings.json";
    link.click();

    toast.success("Settings exported successfully");
  };

  const importSettings = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const settings = JSON.parse(e.target.result);
            if (settings.company) setCompanySettings(settings.company);
            if (settings.payroll) setPayrollSettings(settings.payroll);
            if (settings.notifications)
              setNotificationSettings(settings.notifications);
            if (settings.security) setSecuritySettings(settings.security);
            if (settings.ui) setUiSettings(settings.ui);
            toast.success("Settings imported successfully");
          } catch (error) {
            toast.error("Invalid settings file");
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo file size should be less than 2MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file");
      return;
    }

    // ✅ 1. Keep real File for backend
    setCompanyLogoFile(file);

    // ✅ 2. Generate preview URL (NOT base64)
    const previewUrl = URL.createObjectURL(file);

    setCompanySettings((prev) => ({
      ...prev,
      logo_url: previewUrl, // preview only
    }));

    toast.success("Logo selected");
  };

  const removeLogo = () => {
    if (companySettings?.logo_url?.startsWith("blob:")) {
      URL.revokeObjectURL(companySettings.logo_url);
    }

    setCompanySettings((prev) => ({
      ...prev,
      logo_url: null,
    }));

    setCompanyLogoFile(null);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
  };

  const changePassword = async () => {
    try {
      setChangingPassword(true);

      const response = await changePasswordService(passwordData);

      toast.success(response.message);

      // Reset fields
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-skeleton">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-10 w-72" />
          <div className="flex gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-28" />
            ))}
          </div>
        </div>
        <div className="rounded-xl border p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
          <Skeleton className="h-28 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Configure your payroll system preferences
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save All"}
          </Button>
        </div>
      </div>

      {/* Tabs Navigation - Text with Underline */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Settings Search Input */}
          <div className="relative w-full sm:w-80 mb-3">
            <Input
              type="search"
              placeholder="Search settings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg backdrop-blur-sm max-h-60 overflow-y-auto">
                <ul className="py-1">
                  {searchResults.map((result, index) => (
                    <li
                      key={index}
                      onClick={() => handleResultClick(result)}
                      className="cursor-pointer px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-500/20 hover:text-primary dark:hover:text-primary flex justify-between items-center transition-colors"
                    >
                      <span>{result.name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-md bg-primary/60 dark:bg-primary/60">
                        {result.tab.replace("-", " ").toUpperCase()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
            {canViewTab("company") && (
              <TabsTrigger value="company">
                <Building2 className="h-5 w-5 inline mr-2" />
                <div className="flex flex-col items-start">
                  <span className="text-medium">Company</span>
                  <span className="text-xs">Legal & identity settings</span>
                </div>
              </TabsTrigger>
            )}

            {canViewTab("configurations") && (
              <TabsTrigger value="configurations">
                <SettingsIcon className="h-5 w-5 inline mr-2" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">Configurations</span>
                  <span className="text-xs">Overtime & Ratings</span>
                </div>
              </TabsTrigger>
            )}

            {/* {canViewTab("notifications") && (
              <TabsTrigger value="notifications">
                <Bell className="h-5 w-5 inline mr-2" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">Notifications</span>
                  <span className="text-xs">Emails & Reminders</span>
                </div>
              </TabsTrigger>
            )} */}

            {/* {canViewTab("security") && (
              <TabsTrigger value="security">
                <Lock className="h-5 w-5 inline mr-2" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">Security</span>
                  <span className="text-xs">Authentication & sessions</span>
                </div>
              </TabsTrigger>
            )} */}

            {canViewTab("bank") && (
              <TabsTrigger value="bank">
                <Building2 className="h-5 w-5 inline mr-2" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">Bank & Loans</span>
                  <span className="text-xs">Accounts & Loan Types</span>
                </div>
              </TabsTrigger>
            )}

            {canViewTab("org-structure") && (
              <TabsTrigger value="org-structure">
                <User className="h-5 w-5 inline mr-2" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">Org Structure</span>
                  <span className="text-xs">Departments & Locations</span>
                </div>
              </TabsTrigger>
            )}

            {canViewTab("mail") && (
              <TabsTrigger value="mail">
                <Mail className="h-5 w-5 inline mr-2" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">Mail Settings</span>
                  <span className="text-xs">Custom sender email</span>
                </div>
              </TabsTrigger>
            )}
          </TabsList>

        {/* Company Information Tab */}
        {canViewTab("company") && (
          <TabsContent value="company" className="space-y-6">
            {activeTab === "company" && (
              <CompanySettingsTab
                companySettings={companySettings}
                handleCompanySettingChange={handleCompanySettingChange}
                handleLogoUpload={handleLogoUpload}
                removeLogo={removeLogo}
              />
            )}
          </TabsContent>
        )}

        {/* Payroll Configuration Tab */}
        {canViewTab("configurations") && (
          <TabsContent value="configurations" className="space-y-6">
            {activeTab === "configurations" && (
              <PayrollSettingsTab
                payrollSettings={{
                  ...payrollSettings,
                  isCheckinOutEnabled: companyCheckedInValue,
                }}
                setCompanyEmployeePortalEnabled={
                  setCompanyEmployeePortalEnabled
                }
                companyEmployeePortalEnabled={companyEmployeePortalEnabled}
                handlePayrollSettingChange={handlePayrollSettingChange}
                ratingConfig={ratingConfigDraft}
                setRatingConfig={setRatingConfigDraft}
              />
            )}
          </TabsContent>
        )}

        {/* Notifications Tab */}
        {/* {canViewTab("notifications") && (
          <TabsContent value="notifications" className="space-y-6">
            {activeTab === "notifications" && (
              <NotificationSettingsTab
                notificationSettings={notificationSettings}
                handleNotificationChange={handleNotificationChange}
              />
            )}
          </TabsContent>
        )} */}

        {/* Security & Privacy Tab */}
        {/* {canViewTab("security") && (
          <TabsContent value="security" className="space-y-6">
            {activeTab === "security" && (
              <SecuritySettingsTab
                securitySettings={securitySettings}
                handleSecurityChange={handleSecurityChange}
                changePassword={changePassword}
                handlePasswordChange={handlePasswordChange}
                passwordData={passwordData}
                changingPassword={changingPassword}
                handleUiChange={handleUiChange}
                uiSettings={uiSettings}
              />
            )}
          </TabsContent>
        )} */}

        {canViewTab("bank") && (
          <TabsContent value="bank" className="space-y-6">
            {activeTab === "bank" && (
              <BankAccountsTab
                bankAccounts={bankAccounts}
                setBankAccounts={setBankAccounts}
                companyLoanTypes={allLoanTypes}
                setAllLoanTypes={setAllLoanTypes}
              />
            )}
          </TabsContent>
        )}

        {canViewTab("org-structure") && (
          <TabsContent value="org-structure" className="space-y-6">
            {activeTab === "org-structure" && <ManagerDutiesCard />}
            {activeTab === "org-structure" && (
              <WorkLocationDeptDesignationTab
                locations={locations}
                setLocations={setLocations}
                departments={departments}
                setDepartments={setDepartments}
                designations={designations}
                setDesignations={setDesignations}
              />
            )}
          </TabsContent>
        )}

        {/* Mail Settings Tab — self-contained (own load/save/test/toggle) */}
        {canViewTab("mail") && (
          <TabsContent value="mail" className="space-y-6">
            {activeTab === "mail" && <MailSettingsTab />}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
